import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth.jsx'
import {
  BarChart3, Brain, Calendar, ChevronDown, ClipboardList, FileSearch,
  LayoutDashboard, LogOut, Menu, Search, Shield,
  ShieldAlert, Stethoscope, Users, Video, X
} from 'lucide-react'
import styles from './AppLayout.module.css'

const navGroups = [
  {
    label: 'Practice',
    items: [
      { to: '/app/dashboard', icon: LayoutDashboard, label: 'Clinician Dashboard' },
      { to: '/app/patients', icon: Users, label: 'Patients' },
    ],
  },
  {
    label: 'Clinical pillars',
    items: [
      { to: '/app/clinical', icon: Stethoscope, label: 'Psychiatry' },
      { to: '/app/neurology', icon: Brain, label: 'Neurology' },
      { to: '/app/assessments', icon: ClipboardList, label: 'Scales & Assessments' },
      { to: '/app/safety', icon: ShieldAlert, label: 'Safety Worklist' },
    ],
  },
  {
    label: 'Care operations',
    items: [
      { to: '/app/scheduling', icon: Calendar, label: 'Scheduling' },
      { to: '/app/telemed', icon: Video, label: 'TeleMed' },
      { to: '/app/consultations', icon: FileSearch, label: 'Consultations' },
      { to: '/app/reports', icon: BarChart3, label: 'Reports' },
    ],
  },
]

function initials(user) {
  const name = user?.user_metadata?.full_name || user?.email || 'Clinical User'
  return name.split(/\s|@/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('')
}

export default function AppLayout({ children }) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [patientSearch, setPatientSearch] = useState('')

  const submitSearch = (event) => {
    event.preventDefault()
    const query = patientSearch.trim()
    navigate(query ? `/app/patients?search=${encodeURIComponent(query)}` : '/app/patients')
  }

  return (
    <div className={styles.layout}>
      {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <svg className={styles.logoMark} viewBox="0 0 48 48" role="img" aria-label="PatientTrac networked P">
            <defs>
              <linearGradient id="mind-mark" x1="7" y1="5" x2="41" y2="43" gradientUnits="userSpaceOnUse">
                <stop stopColor="#00d4ff" />
                <stop offset="1" stopColor="#c9a96e" />
              </linearGradient>
            </defs>
            <rect x="1" y="1" width="46" height="46" rx="12" fill="#060e1c" stroke="rgba(201,169,110,.55)" />
            <path d="M14 11h14c8 0 13 5 13 12s-5 12-13 12h-6v8h-8V11Zm8 7v10h5.5c3.7 0 5.5-1.7 5.5-5s-1.8-5-5.5-5H22Z" fill="url(#mind-mark)" fillRule="evenodd" />
            <path d="m19 19 9 4m-9 10 9-10m0 0 8 7" stroke="#e8cc9a" strokeWidth="1.2" opacity=".85" />
            <circle cx="19" cy="19" r="1.8" fill="#f4f7fb" /><circle cx="19" cy="33" r="1.8" fill="#f4f7fb" /><circle cx="28" cy="23" r="2.1" fill="#c9a96e" /><circle cx="36" cy="30" r="1.8" fill="#00d4ff" />
          </svg>
          <div className={styles.logoText}>
            <span className={styles.logoTitle}>PatientTrac</span>
            <span className={styles.logoSub}>Mind</span>
          </div>
          <button className={styles.closeBtn} onClick={() => setSidebarOpen(false)} aria-label="Close navigation"><X size={18} /></button>
        </div>

        <nav className={styles.nav} aria-label="Clinical navigation">
          {navGroups.map((group) => (
            <div className={styles.navGroup} key={group.label}>
              <span className={styles.navLabel}>{group.label}</span>
              {group.items.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={17} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.hipaaTag}><Shield size={12} /><span>Protected clinical workspace</span></div>
          <p className={styles.version}>PatientTrac Mind · v1.0.0</p>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <button className={styles.menuBtn} onClick={() => setSidebarOpen(true)} aria-label="Open navigation"><Menu size={20} /></button>

          <form className={styles.patientSearch} onSubmit={submitSearch} role="search">
            <Search size={16} />
            <label className="sr-only" htmlFor="global-patient-search">Find a patient</label>
            <input id="global-patient-search" value={patientSearch} onChange={(event) => setPatientSearch(event.target.value)} placeholder="Find patient by name or identifier" />
          </form>

          <div className={styles.headerActions}>
            <span className={styles.workspaceLabel}>Neurobehavioral Health EMR</span>
            <div className={styles.userMenu}>
              <button className={styles.userBtn} onClick={() => setUserMenuOpen(!userMenuOpen)} aria-expanded={userMenuOpen}>
                <span className={styles.avatar} aria-hidden="true">{initials(user)}</span>
                <span className={styles.userName}>{user?.user_metadata?.full_name || user?.email || 'Clinician'}</span>
                <ChevronDown size={14} />
              </button>

              {userMenuOpen && (
                <div className={styles.dropdown}>
                  <div className={styles.dropdownHeader}>
                    <p className={styles.dropdownName}>{user?.user_metadata?.full_name || 'Clinical user'}</p>
                    <p className={styles.dropdownEmail}>{user?.email}</p>
                  </div>
                  <button className={styles.dropdownItem} onClick={signOut}><LogOut size={14} /> Sign out securely</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className={styles.content}>{children || <Outlet />}</main>

        <footer className="hipaa-notice">
          PatientTrac Mind contains Protected Health Information. Access is monitored and logged in accordance with organizational security policy. © {new Date().getFullYear()} PatientTrac.
        </footer>
      </div>
    </div>
  )
}
